import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DataLoaPasca, PaginationData } from "@/types";
import { Pagination } from "./Pagination";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "@/hooks/useDebounce";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  paginationData: PaginationData<DataLoaPasca[]>;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  );
  const statusParams = searchParams.get("status") || "";
  const debouncedQuery = useDebounce(searchQuery, 1000);
  const navigate = useNavigate();
  const location = useLocation();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (debouncedQuery) {
      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery);
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search]);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <div className="flex-1 flex items-center gap-3">
          <Input
            autoFocus
            placeholder="Filter by nomor pendaftaran atau nama lengkap"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="max-w-sm"
          />
          <button className="rounded-full border flex text-sm font-semibold overflow-hidden">
            <div
              className={`py-2 px-4 whitespace-nowrap ${
                statusParams === "0"
                  ? "bg-black/90 text-white rounded-full"
                  : "hover:bg-secondary"
              }`}
              onClick={() => {
                if (statusParams) searchParams.delete("status");
                searchParams.set("status", "0");

                navigate(`${location.pathname}?${searchParams.toString()}`);
              }}
            >
              Menunggu persetujuan
            </div>
            <div
              className={`py-2 px-4 whitespace-nowrap ${
                statusParams === "1"
                  ? "bg-black/90 text-white rounded-full"
                  : "hover:bg-secondary"
              }`}
              onClick={() => {
                if (statusParams) searchParams.delete("status");
                searchParams.set("status", "1");

                navigate(`${location.pathname}?${searchParams.toString()}`);
              }}
            >
              Sudah Disetujui
            </div>
            <div
              className={`py-2 px-4 whitespace-nowrap ${
                !statusParams
                  ? "bg-black/90 text-white rounded-full"
                  : "hover:bg-secondary"
              }`}
              onClick={() => {
                if (statusParams) searchParams.delete("status");

                navigate(`${location.pathname}?${searchParams.toString()}`);
              }}
            >
              Semua
            </div>
          </button>
        </div>
        <div className="flex gap-x-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>
    </div>
  );
}
